import { useState, useEffect, useCallback } from 'react';

interface UseCancelTimerProps {
  createdAt: string;
  onTimeUp?: () => void;
}

interface UseCancelTimerReturn {
  canCancel: boolean;
  remainingSeconds: number;
  remainingTimeText: string;
}

/**
 * 送信取消タイマーのカスタムフック
 * 作成日時から3分間の送信取消可能期間を管理し、リアルタイムで残り時間を表示
 */
export const useCancelTimer = ({
  createdAt,
  onTimeUp,
}: UseCancelTimerProps): UseCancelTimerReturn => {
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  
  // 残り秒数を計算
  const calculateRemainingSeconds = useCallback((now: Date): number => {
    const createdTime = new Date(createdAt);
    const elapsedMilliseconds = now.getTime() - createdTime.getTime();
    const elapsedSeconds = Math.floor(elapsedMilliseconds / 1000);
    const remainingSeconds = Math.max(0, 180 - elapsedSeconds); // 180秒 = 3分
    
    return remainingSeconds;
  }, [createdAt]);
  
  const remainingSeconds = calculateRemainingSeconds(currentTime);
  const canCancel = remainingSeconds > 0;
  
  // 残り時間のテキスト表示を生成
  const remainingTimeText = useCallback((seconds: number): string => {
    if (seconds <= 0) {
      return '送信完了';
    }
    
    const minutes = Math.floor(seconds / 60);
    const remainingSecondsOnly = seconds % 60;
    
    if (minutes > 0) {
      return `残り${minutes}分${remainingSecondsOnly.toString().padStart(2, '0')}秒`;
    } else {
      return `残り${remainingSecondsOnly}秒`;
    }
  }, []);
  
  // 1秒ごとに現在時刻を更新
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);
      
      // 時間切れの場合、コールバックを実行
      const remaining = calculateRemainingSeconds(now);
      if (remaining === 0 && onTimeUp) {
        onTimeUp();
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [calculateRemainingSeconds, onTimeUp]);
  
  return {
    canCancel,
    remainingSeconds,
    remainingTimeText: remainingTimeText(remainingSeconds),
  };
};